import { memo, useState } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { Button, Input, Timeline } from 'antd';
import dayjs from 'lib/dayjs';
import { css } from '@emotion/css';
import SiderImageSlide from 'views/Conversation/Sider/SiderImageSlide';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import Highlighter from 'react-highlight-words';
import TransComponent from '../../../../components/TransComponent';
import { T, useTranslate } from '@tolgee/react';
import type { BeforeAfterContent } from './operationLogHooks';
import {
  useOperationLogItems,
  useKeywordFilterVisibility,
  useOperationTitle,
  useBeforeAfterContent,
  useItemDisplayInfo,
} from './operationLogHooks';

type TodoRecordImplementProps = {
  dataSource: Todo.TodoInstance['operationLog'];
  simple: boolean;
  type: Todo.Type;
  storeId: string;
};

/**
 * TodoRecord 待办的变更记录
 * @constructor
 */
export function TodoRecordImplement(props: TodoRecordImplementProps) {
  const { simple, dataSource, type, storeId } = props;
  const { t } = useTranslate();
  const [keyword, setKeyword] = useState('');
  const { itemsToShow, getShowDetail } = useOperationLogItems(dataSource, simple);

  return (
    <div className="!max-h-[60vh]">
      {!simple && (
        <div className="mb-[16px]">
          <Input.Search placeholder={t('search.searchIptTip')} onSearch={setKeyword} />
        </div>
      )}

      <div>
        <Timeline>
          {itemsToShow.map((item, index) => {
            const { createTime } = item;
            const showDetail = getShowDetail(item);
            return (
              <TimelineItemContainer key={index} keyword={keyword}>
                <Timeline.Item>
                  <div>
                    <div className={`flex flex-start ${simple ? 'flex-col' : 'justify-between'}`}>
                      <div className="text[#232426] leading-[20px]">
                        <Title data={item} keyword={keyword} />
                      </div>
                      <div className="text-[12px] text-[#84868c] leading-[18px]">
                        {dayjs(Number(createTime)).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                    {showDetail && (
                      <div className="bg-[#f7f9fa] p-[8px] rounded-[4px] text-[#515359] mt-[8px]">
                        <div className="flex items-start mb-[8px]">
                          <div className="flex-shrink-0 mr-[6px] leading-1">
                            <T
                              keyName="knowledge.修改前x"
                              params={{
                                x: <TransComponent content={<>:</>} />,
                              }}
                            />
                          </div>
                          <div className="flex-grow">
                            <BeforeAndAfter
                              data={item}
                              when="before"
                              keyword={keyword}
                              type={type}
                              storeId={storeId}
                            />
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mr-[6px]">
                            <T
                              keyName="knowledge.修改后x"
                              params={{
                                x: <TransComponent content={<>:</>} />,
                              }}
                            />
                          </div>
                          <div className="flex-grow">
                            <BeforeAndAfter
                              data={item}
                              when="after"
                              keyword={keyword}
                              type={type}
                              storeId={storeId}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              </TimelineItemContainer>
            );
          })}
        </Timeline>
      </div>
    </div>
  );
}

function TimelineItemContainer(props: { children: React.ReactNode; keyword: string }) {
  const { children, keyword } = props;
  const { ref, visible } = useKeywordFilterVisibility(keyword);
  const hiddenStyle = visible ? {} : { style: { height: '0px', overflow: 'hidden' } };
  return (
    <div
      className={css`
        &:last-of-type {
          .ant-timeline-item-tail {
            display: none;
          }
        }
      `}
      {...hiddenStyle}
      ref={ref}
    >
      {children}
    </div>
  );
}

function renderBeforeAfterContent(
  content: BeforeAfterContent,
  keyword: string,
  storeId: string,
): React.ReactNode {
  if (content.kind === 'none') return null;

  const imageBlock = (images: string[]) =>
    images?.length > 0 ? (
      <div className="h-[56px] w-[172px]">
        <PreviewModalProvider>
          <SiderImageSlide
            videos={[]}
            width={172}
            images={images}
            count={3}
            index={0}
            offsetPercent={2}
          />
        </PreviewModalProvider>
      </div>
    ) : null;

  if (content.kind === 'textAndImages') {
    return (
      <div className="leading-1">
        {imageBlock(content.images)}
        <div>
          <Highlighter autoEscape searchWords={[keyword]} textToHighlight={content.text} />
        </div>
      </div>
    );
  }
  if (content.kind === 'tradeId') {
    return (
      <div>
        [<Highlighter autoEscape searchWords={[keyword]} textToHighlight={content.text} />]
      </div>
    );
  }
  if (content.kind === 'itemOrSkus') {
    if (content.itemId) {
      return (
        <div>
          <HighlighterItem id={content.itemId} keyword={keyword} storeId={storeId} />
        </div>
      );
    }
    if (content.skus?.length) {
      return (
        <div>
          {content.skus.map((item, index) => (
            <HighlighterItem
              key={index}
              id={item.itemId}
              keyword={keyword}
              skuId={item.skuId}
              quantity={item.quantity}
              storeId={storeId}
            />
          ))}
        </div>
      );
    }
    return null;
  }
  if (content.kind === 'result') {
    return (
      <div className="leading-1">
        {imageBlock(content.images)}
        <div>
          <Highlighter autoEscape searchWords={[keyword]} textToHighlight={content.text || ''} />
        </div>
      </div>
    );
  }
  return null;
}

function BeforeAndAfter(props: {
  data: Todo.TodoInstance['operationLog'][number];
  when: 'before' | 'after';
  keyword: string;
  type: Todo.Type;
  storeId: string;
}) {
  const { data, when, keyword, type, storeId } = props;
  const content = useBeforeAfterContent(data, when, type);
  return <>{renderBeforeAfterContent(content, keyword, storeId)}</>;
}

interface HighlighterItemProps {
  id: string;
  keyword: string;
  skuId?: string;
  quantity?: number;
  storeId?: string;
}

const HighlighterItem = memo((props: HighlighterItemProps) => {
  const { id, keyword, quantity, skuId, storeId = '' } = props;
  const { displayText } = useItemDisplayInfo(id, storeId, skuId, quantity);
  if (!displayText) return null;
  return <Highlighter autoEscape searchWords={[keyword]} textToHighlight={displayText} />;
});

const Title = memo(
  (props: { data: Todo.TodoInstance['operationLog'][number]; keyword: string }) => {
    const { keyword, data } = props;
    const title = useOperationTitle(data);
    return <Highlighter autoEscape searchWords={[keyword]} textToHighlight={title} />;
  },
);

type TodoRecordProps = {
  visible: boolean;
  onCancel: () => void;
  dataSource: Todo.TodoInstance['operationLog'];
  type: number;
  storeId: string;
};

// 以组件的形式提供
export const TodoRecord = memo((props: TodoRecordProps) => {
  const { visible, onCancel, dataSource, type, storeId } = props;
  const { t } = useTranslate();
  return (
    <JustRightHeightModal
      visible={visible}
      onCancel={onCancel}
      title={t('scene.chatWorkspace.taskTab.action.viewDetail')}
      width={662}
      footer={<Button onClick={onCancel}>{t('marketing.关闭')}</Button>}
    >
      <TodoRecordImplement simple={false} dataSource={dataSource} type={type} storeId={storeId} />
    </JustRightHeightModal>
  );
});
